<template>
  <div class="lei-row" :class="{ 'lei-row--stale': isStale, 'lei-row--busy': busy }">
    <div class="lei-row__head" :class="{ 'lei-row__head--edit': reorderMode }">
      <span v-if="reorderMode" class="lei-drag" :class="{ 'lei-drag--off': !channelRecord }"
            :title="channelRecord ? 'Drag to reorder' : 'Not found in channels, can\'t be reordered'">⋮⋮</span>
      <span class="lei-row__num lei-mono">{{ orderLabel }}</span>
      <div class="lei-row__line lei-row__line--top">
      <div class="lei-row__id">
        <span class="lei-row__name lei-mono">{{ channel.channelName || '—' }}</span>
        <span v-if="channel.language" class="lei-row__lang">{{ channel.language }}</span>
      </div>
      <div class="lei-row__flags">
        <span v-if="!channelRecord && channelRecordsLoaded" class="lei-row__missing">not found in channels</span>
        <template v-else-if="channelRecord">
          <span class="lei-flag" :class="channelRecord.status ? 'lei-flag--on' : 'lei-flag--off'">{{ channelRecord.status ? 'status on' : 'status off' }}</span>
          <button type="button" class="lei-flag lei-flag--btn" :class="channelRecord.active ? 'lei-flag--on' : 'lei-flag--off'"
                  :disabled="busy" :title="channelRecord.active ? 'Click to deactivate (hide from users)' : 'Click to activate'"
                  @click="$emit('toggle-active', channelRecord)">
            {{ channelRecord.active ? 'active' : 'inactive' }}
          </button>
          <span v-if="mismatch" class="lei-mismatch">{{ mismatch }}</span>
        </template>
      </div>
      </div>
      <div class="lei-row__line lei-row__line--bottom">
      <span v-if="channelRecord" class="lei-row__views lei-mono" :class="{ 'lei-muted': !channelRecord.status }">
        <template v-if="channelRecord.status"><b>{{ viewsLabel }}</b> live viewers</template>
        <template v-else>— live viewers</template>
      </span>
      <div class="lei-row__actions">
        <button v-if="canStart" type="button" class="lei-btn lei-btn--sm lei-btn--start"
                :disabled="busy || actionsLocked" :title="actionsLocked ? lockedTitle : 'Start this stream on the encoder'" @click="$emit('start', channel)">
          Start
        </button>
        <button v-if="running" type="button" class="lei-btn lei-btn--sm lei-btn--danger"
                :disabled="busy || actionsLocked || !!stopDisabledReason" :title="actionsLocked ? lockedTitle : (stopDisabledReason || 'Stop this stream on the encoder')"
                @click="$emit('stop', channel)">
          Stop
        </button>
        <button v-if="linked" type="button" class="lei-btn lei-btn--sm" :class="{ 'is-open': infoOpen }" @click="toggleInfo">
          {{ infoOpen ? 'Less info' : 'More info' }}
        </button>
        <button v-if="channel.channelName" type="button" class="lei-btn lei-btn--sm lei-btn--icon" title="Open Kibana dashboard" @click="openKibana">
          <img src="@/assets/chart-line.svg" alt="Kibana" />
        </button>
      </div>
      </div>
    </div>
    <div v-if="actionError" class="lei-row__error">{{ actionError }}</div>
    <div class="lei-strip" :class="'lei-strip--' + tone">
      <div class="lei-strip__state">
        <span class="lei-badge" :class="'lei-badge--' + tone">{{ badgeLabel }}</span>
        <span v-if="stateNote" class="lei-strip__note">{{ stateNote }}</span>
      </div>
      <div v-if="state === 'not-linked'" class="lei-strip__body lei-muted">No encoder or stream id set on this channel</div>
      <template v-else>
        <div class="lei-strip__body">
          <span class="lei-strip__name">{{ channel.encStreamName }}</span> <span v-if="statsLine" class="lei-strip__stats lei-mono">{{ statsLine }}</span>
        </div>
        <div class="lei-strip__ids lei-mono">
          <span>{{ channel.encoder }}<template v-if="channel.origin"> &rarr;</template></span> <span v-if="channel.origin">{{ channel.origin }}</span>
        </div>
      </template>
    </div>
    <div v-if="configIssues.length" class="lei-row__config">
      <div v-for="(issue, idx) in configIssues" :key="idx" class="lei-info__warn">{{ issue }}</div>
    </div>
    <div v-if="infoOpen" class="lei-info">
      <div v-if="infoLoading" class="lei-muted">Loading from {{ channel.encoder }}…</div>
      <div v-else-if="infoError" class="lei-row__error">{{ infoError }}</div>
      <template v-else-if="info">
        <div v-if="outputMismatch" class="lei-info__warn">{{ outputMismatch }}</div>
        <div class="lei-info__grid">
          <span class="lei-muted">Status</span><span>{{ info.status || '—' }}</span>
          <span class="lei-muted">Stream</span><span class="lei-mono">{{ info.name || '—' }} <span class="lei-muted">#{{ info.id || channel.encStreamId }}</span></span>
          <span class="lei-muted">Inputs</span>
          <span>
            <span v-if="!inputs.length" class="lei-muted">none</span>
            <span v-for="(inp, idx) in inputs" :key="idx" class="lei-info__url lei-mono">{{ idx + 1 }}: {{ inp }}</span>
          </span>
          <span class="lei-muted">Output</span><span class="lei-info__url lei-mono">{{ info.output || '—' }}</span>
        </div>
        <div class="lei-info__foot lei-muted">
          fetched {{ infoAt }}
          <button type="button" class="lei-btn lei-btn--sm" :disabled="infoLoading" @click="loadInfo">Reload</button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import XaccelService from '@/services/xaccel-service';
import {
  isLinkedChannel, isStreamRunning, formatBitrate, formatUptime, formatSpeed, formatStatInt,
  extractStreamNameFromUrl, kibanaDashboardUrl, encStreamIdentifier
} from '@/utils/bapi-functions';

const EMPTY_STATS = '— · — fps · — · — · — errors';

export default {
  name: 'LiveChannelRow',
  emits: ['stop', 'start', 'toggle-active'],
  props: {
    channel: { type: Object, required: true },
    encoderStateEntry: { type: Object, default: null },
    channelRecord: { type: Object, default: null },
    channelRecordsLoaded: { type: Boolean, default: false },
    reorderMode: { type: Boolean, default: false },
    // True while any match is being reordered - Start/Stop stay visible but disabled.
    actionsLocked: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
    shouldBeLive: { type: Boolean, default: false },
    actionError: { type: String, default: '' },
    configIssues: { type: Array, default: () => [] }
  },
  data() {
    return {
      infoOpen: false,
      infoLoading: false,
      infoError: '',
      info: null,
      infoAt: ''
    };
  },
  computed: {
    lockedTitle() {
      return 'Finish reordering (click Done) to start or stop streams';
    },
    linked() {
      return isLinkedChannel(this.channel);
    },
    orderLabel() {
      const n = this.channelRecord?.orderNumber;
      return n !== null && n !== undefined ? n : '—';
    },
    viewsLabel() {
      return Number(this.channelRecord?.liveViews || 0).toLocaleString();
    },
    stat() {
      const entry = this.encoderStateEntry;
      if (!entry || !entry.stats) return null;
      return entry.stats.get(String(this.channel.encStreamId || '').trim()) || null;
    },
    running() {
      return !!this.stat && isStreamRunning(this.stat.status);
    },
    canStart() {
      return this.shouldBeLive && this.state === 'stopped' && !this.encoderStateEntry?.unreachable;
    },
    stopDisabledReason() {
      if (this.encoderStateEntry?.unreachable) return 'Encoder unreachable';
      return '';
    },
    state() {
      if (!this.linked) return 'not-linked';
      const entry = this.encoderStateEntry;
      if (!entry || entry.pending) return 'loading';
      if (this.stat) return this.running ? 'streaming' : 'stopped';
      return entry.unreachable ? 'unreachable' : 'no-data';
    },
    isStale() {
      return !!this.stat && !!this.encoderStateEntry && this.encoderStateEntry.unreachable;
    },
    tone() {
      switch (this.state) {
        case 'streaming': return 'ok';
        case 'stopped': return 'bad';
        case 'no-data':
        case 'unreachable': return 'warn';
        default: return 'none';
      }
    },
    stateLabel() {
      const labels = {
        'streaming': 'streaming',
        'stopped': 'stopped',
        'no-data': 'no data',
        'unreachable': 'unreachable',
        'not-linked': 'not linked',
        'loading': 'loading…'
      };
      return labels[this.state];
    },
    badgeLabel() {
      return this.state === 'streaming' ? 'live' : this.stateLabel;
    },
    stateNote() {
      if (this.isStale) {
        const at = this.encoderStateEntry.lastOkAt;
        return at ? 'stale · ' + at.toLocaleTimeString(undefined, { hour12: false }) : 'stale';
      }
      if (this.state === 'no-data') return 'id not in encoder list';
      if (this.state === 'unreachable') return 'no last-known values';
      return '';
    },
    mismatch() {
      if (!this.channelRecord || !['streaming', 'stopped', 'no-data'].includes(this.state)) return '';
      if (this.channelRecord.status && !this.running) return 'status on, encoder ' + this.stateLabel;
      if (!this.channelRecord.status && this.running) return 'encoder streaming, status off';
      return '';
    },
    statsLine() {
      if (this.state === 'loading') return '';
      if (!this.running) return EMPTY_STATS;
      const s = this.stat;
      return [
        formatBitrate(s.bitrate),
        formatStatInt(s.fps) + ' fps',
        formatUptime(s.uptime),
        formatSpeed(s.speed),
        formatStatInt(s.errors) + ' errors'
      ].join(' · ');
    },
    inputs() {
      const list = Array.isArray(this.info?.inputs) ? this.info.inputs : [];
      return list.map(inp => (typeof inp === 'string' ? inp : (inp?.url || JSON.stringify(inp))));
    },
    outputMismatch() {
      if (!this.info) return '';
      if (!this.info.output) return 'Encoder stream has no output URL.';
      const outName = extractStreamNameFromUrl(this.info.output);
      if (outName && this.channel.channelName && outName !== this.channel.channelName) {
        return `Encoder output points to ${outName}, this channel is ${this.channel.channelName}.`;
      }
      return '';
    }
  },
  methods: {
    toggleInfo() {
      this.infoOpen = !this.infoOpen;
      if (this.infoOpen) {
        this.loadInfo();
      } else {
        this.info = null;
        this.infoError = '';
      }
    },
    loadInfo() {
      this.infoLoading = true;
      this.infoError = '';
      XaccelService.getXaccelStreamMetadata(this.channel.encoder.trim(), encStreamIdentifier(this.channel.encStreamId))
        .then((res) => {
          this.info = res || {};
          this.infoAt = new Date().toLocaleTimeString(undefined, { hour12: false });
        })
        .catch((err) => {
          console.error('LiveChannelRow: error loading stream info', err);
          this.infoError = 'Could not load stream info from ' + this.channel.encoder + '.';
        })
        .finally(() => {
          this.infoLoading = false;
        });
    },
    openKibana() {
      const url = kibanaDashboardUrl(this.channel.channelName);
      if (url) window.open(url, '_blank');
    }
  }
};
</script>
<style src="../assets/style.css"></style>
